import { toast } from 'sonner';
import React from 'react';
import { CheckCircle2, Download as DownloadIcon, AlertCircle } from 'lucide-react';

/** Small progress-bar toast rendered inside sonner. */
const DownloadToast = ({ label, pct, status }) => {
  const color = status === 'error' ? 'bg-red-500'
    : status === 'success' ? 'bg-green-500'
    : 'bg-[#c9a43a]';
  const Icon = status === 'success' ? CheckCircle2
    : status === 'error' ? AlertCircle
    : DownloadIcon;
  return (
    <div className="flex flex-col gap-2 min-w-[240px]">
      <div className="flex items-center gap-2 text-sm text-[#0f1729]">
        <Icon className={`w-4 h-4 ${status === 'success' ? 'text-green-600' : status === 'error' ? 'text-red-600' : 'text-[#9a7b24]'}`} />
        <span className="font-semibold truncate flex-1" title={label}>
          {status === 'success' ? `Downloaded · ${label}` : status === 'error' ? `Failed · ${label}` : `Downloading ${label}…`}
        </span>
        <span className="text-xs text-[#8a8f99] tabular-nums">{Math.round(pct)}%</span>
      </div>
      <div className="w-full h-1.5 bg-[#e5e5e5] rounded-full overflow-hidden">
        <div className={`h-full ${color} transition-all duration-200`} style={{ width: `${Math.max(4, pct)}%` }} />
      </div>
    </div>
  );
};

/** True on phones / tablets / native WebView wrappers (Median, GoNative,
 * Android `wv`, iPadOS-as-Mac). On these, blob-URL `<a download>` clicks
 * are silently swallowed by the WebView — the reason "downloads don't
 * work on Pixel". They need a REAL navigation to a same-origin URL with
 * `Content-Disposition: attachment` so the native download manager
 * takes over. */
export const isMobileOrWebView = () => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  if (/Android|iPhone|iPad|iPod|Mobi|wv\)|Median|GoNative/i.test(ua)) return true;
  // iPadOS 13+ masquerades as desktop Mac but has multitouch.
  return /Macintosh/.test(ua) && (navigator.maxTouchPoints || 0) > 1;
};

/** Fetch `url` and save it as a file, showing a live progress-bar toast.
 * Works cross-browser, no new-tab required.
 *
 * On mobile / WebView we skip the fetch+blob path entirely and click a
 * real anchor at `directUrl` (falls back to `url`) — the server's
 * attachment disposition hands the file to the native download manager,
 * which is the only reliable path on Android WebViews and iOS Safari.
 *
 * @param {string} url
 * @param {string} filename
 * @param {string} label
 * @param {(blob: Blob) => Promise<void>|void} onBlob - optional hook
 *   called with the final Blob before it's saved to disk. Used by the
 *   offline-audio cache to keep a copy in IndexedDB.
 * @param {string|null} directUrl - attachment-disposition URL used for
 *   the mobile/WebView direct-navigation path.
 * @returns {Promise<boolean>} true on success, false on failure.
 */
/** iOS home-screen PWA: `<a download>` either does nothing or opens Quick
 * Look with no way back. A `window.open` lands in the in-app Safari sheet
 * (with a Done button) where the attachment download prompt works. */
const isIosStandalone = () =>
  typeof navigator !== 'undefined' && navigator.standalone === true;

export async function downloadWithProgress(url, filename, label = filename, onBlob = null, directUrl = null) {
  if (isMobileOrWebView()) {
    try {
      const href = directUrl || url;
      if (isIosStandalone()) {
        window.open(href, '_blank', 'noopener');
      } else {
        const a = document.createElement('a');
        a.href = href;
        a.download = filename;
        a.rel = 'noopener';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
      toast.success(`Download started · ${label}`, { duration: 3500, position: 'bottom-right' });
      return true;
    } catch {
      /* fall through to the fetch path below */
    }
  }
  const toastId = toast.custom(
    () => <DownloadToast label={label} pct={0} status="progress" />,
    { duration: Infinity, position: 'bottom-right' }
  );
  const update = (pct, status = 'progress') => {
    toast.custom(() => <DownloadToast label={label} pct={pct} status={status} />, {
      id: toastId,
      duration: status === 'progress' ? Infinity : 2500,
      position: 'bottom-right',
    });
  };

  try {
    const resp = await fetch(url, { credentials: 'omit' });
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    const total = parseInt(resp.headers.get('content-length') || '0', 10);
    // If the server didn't send a Content-Length (Drive often doesn't for
    // streamed downloads), we run a spinner-style indeterminate bar that
    // ticks based on bytes-received, capping at 90% until the last chunk.
    const reader = resp.body?.getReader();
    if (!reader) {
      // Fall back to non-streamed blob if streaming isn't supported.
      const blob = await resp.blob();
      if (onBlob) { try { await onBlob(blob); } catch { /* ignore */ } }
      _save(blob, filename);
      update(100, 'success');
      return true;
    }

    const chunks = [];
    let received = 0;
    while (true) {  // eslint-disable-line no-constant-condition
      const { done, value } = await reader.read();
      if (done) break;
      chunks.push(value);
      received += value.length;
      const pct = total > 0
        ? Math.min(99, (received / total) * 100)
        : Math.min(90, (received / (5 * 1024 * 1024)) * 100);  // assume ~5MB baseline
      update(pct);
    }
    const blob = new Blob(chunks);
    if (onBlob) { try { await onBlob(blob); } catch { /* ignore */ } }
    _save(blob, filename);
    update(100, 'success');
    return true;
  } catch (e) {
    update(0, 'error');
    return false;
  }
}

function _save(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 30000);
}
